// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
//
// ### CompleteTheMeasure
// ## Fill in the missing rhythm of a measure
//
// Include description of object & useful info here, please!
import { defaultsDeep } from 'shared/src/utils/defaultsDeep';
import type {
  InteractiveSettings,
  LayoutSettings,
  LayoutItem,
  InteractiveOptions,
} from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import config from '../config';
import Music from '../uTheory.Notation/music';
import CompleteTheMeasureQuestion from './completeTheMeasureQuestion';
import utils from '../utils';
import Editable from '../uTheory.Notation/editable';
import NoteHolder from '../uTheory.Notation/noteHolder';
import MeterID from './meterID';
import MusicalClock from '../uTheory.Notation/musicalClock';
import * as Skills from '../uTheory.Skills/skills';
import type { AnswerAndData } from './interactiveQuestionPrototype';
import Rhythm from '../uTheory.Notation/rhythm';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';
import CompleteTheMeasureButtons from './CompleteTheMeasureButtons.svelte';
import type { BaseDuration } from 'shared/definitions/musicTheory/rhythms';

const EDITOR_BUFFER = 2;

interface QuestionObject {
  musicString: string;
  id: string | null;
}

interface Layout extends LayoutSettings {
  rhythmValues: LayoutItem;
}

export default class CompleteTheMeasure extends InteractivePrototype {
  declare layout: Layout;

  declare settings: InteractiveSettings;

  declare currentQuestion: CompleteTheMeasureQuestion;

  declare questions: CompleteTheMeasureQuestion[];

  /** The number of notes we expect total in the measure when the user is done... */
  private expectedNotes: number = 0;

  declare questionData: QuestionObject[];

  protected musicalClock!: MusicalClock;

  /**
   * Initialized in newQuestion()
   */
  protected questionSVG?: string;

  constructor(displayDiv: HTMLDivElement, options: InteractiveOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = CompleteTheMeasure.registry.skills;
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: InteractiveOptions): Promise<any> {
    this.musicalClock = new MusicalClock();

    this.settings = {
      instructions: true,
      editor: {
        fixedWidth: 220,
        maxMeasurePadding: 800,
        ariaTitle: {
          letterName: false,
          octave: false,
          accidental: false,
          keySignature: false,
          measureNumber: false,
          theWordNote: false,
        },
        ariaLive: 'assertive',
        disableTextInput: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      uPrefs: options.drill ? { appName: 'completeTheMeasure', optional: false } : false,
      questionData: options.drill && {
        filename: 'measure-rhythms',
        directory: `${config.apiUrl}/${config.questionsDirectory}`,
      },
    };

    if (options.drill && this.settings.questionData) {
      if (!Array.isArray(options.skills)) {
        throw new Error('options.skills array must be given in drill mode.');
      }

      const skills: string[] = options.skills.map(Skills.makeString);
      const includeSimple = skills.includes('meter_completion_simple');
      const includeCompound = skills.includes('meter_completion_compound');
      const includeCutTime = skills.includes('meter_completion_cut_time');

      this.settings.questionData.filename += utils.uriEncode({
        includeSimple,
        includeCompound,
        includeCutTime,
        appName: 'completeTheMeasure',
      });
    }

    this.layout = {
      instructions: { percent: 20, maxPercent: 20, noWrap: true, style: { marginBottom: '0.1em' } },
      editor: { percent: 40, greedy: true },
      rhythmValues: {
        percent: 40,
        maxPercent: 40,
        visible: true,
        name: 'RhythmPanel',
      },
    };

    const loadingPromise = super.init(displayDiv, options);

    return loadingPromise;
  }

  rhythmButtonPanel: CompleteTheMeasureButtons;

  rhythmValuesDiv: HTMLDivElement;

  createCustomDivs(): void {
    const rhythmValuesDiv = this.createDiv(
      'rhythmValuesDiv',
      {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
      },
      true
    );

    this.displayDiv.insertBefore(rhythmValuesDiv, this.lettersDiv!);
    this.rhythmButtonPanel = new CompleteTheMeasureButtons({
      target: rhythmValuesDiv,
      props: {
        class: 'w-fit mx-auto',
        submitText: this.options.testMode ? 'Submit' : 'Check',
      },
    });

    this.rhythmButtonPanel.$on('click', (evt: CustomEvent<string>) => this.handleClick(evt.detail));

    this.layout.rhythmValues.div = rhythmValuesDiv;
    this.layout.rhythmValues.callback = (height: number, div: HTMLDivElement) => {
      return height - div.clientHeight;
    };
  }

  initializeMusic(): void {
    this.music = Music.fromString(`meter:4/4 key:C/Major
clef:percussion B4/w |`);
  }

  /**
   * Gets the last editable noteHolder on the staff. If hasNote is true, gets last editable noteHolder that has a note
   */
  protected getLastEditableNoteHolder(hasNote = false): NoteHolder {
    const { editables } = this.editor!;
    const lastEditableNH = editables[editables.length - 1]?.noteHolder;
    if (!lastEditableNH) {
      throw Error(
        `Unable to get last editable note holder in complete the measure! ${this.music.toString()}`
      );
    }
    if (hasNote) {
      const penultimateEditableNH = editables[editables.length - 2]?.noteHolder;
      if (lastEditableNH.isNeitherNoteNorRest() && !penultimateEditableNH) {
        throw new Error(
          'Last editable note holder is not a note or rest, and there is no penultimate editable note holder.'
        );
      }
      return lastEditableNH.isNeitherNoteNorRest() ? penultimateEditableNH! : lastEditableNH;
    }
    return lastEditableNH;
  }

  /**
   * Update the selected notes to whatever rhythmValue is inputted
   */
  protected updateAnswer(rhythmValue: string): void {
    // Update the chosen nh
    this.editor!.editables.forEach((editable) => {
      if (editable.selected || editable.selectedNotes.length !== 0) {
        editable.noteHolder.rhythm?.setDuration(rhythmValue);
      }
    });

    this.editor!.renderer.render();
  }

  /**
   * Delete the selected note, if no note is selected, delete the last inputted note on the measure
   */
  protected deleteNote(): void {
    const noteHolder = this.editor!.getSelected()[0];
    const measure = this.music.voices[0]!.measures[0]!;
    if (noteHolder) measure.splice(noteHolder, 1);
    else if (this.editor!.editables.length <= 1) return;
    else measure.splice(this.getLastEditableNoteHolder(true), 1);
    this.resizeEditor();
    this.editor!.renderer.render();
  }

  /**
   * Remove any Ghosting NoteHolder on the staff without rerendering
   */
  protected clearGhostingNH(): void {
    const measure = this.music.voices[0]!.measures[0]!;
    const newNH = measure.noteHolders.filter((nh) => !nh.isNeitherNoteNorRest());
    measure.noteHolders = newNH;
  }

  private renderAndUpdateButtonState() {
    this.editor!.deselectAll();
    this.clearGhostingNH();
    this.resizeEditor();
    this.editor!.renderer.render();
    this.disableOrEnableButtons();
  }

  protected handleClick(str: BaseDuration | `${BaseDuration}r` | 'check' | 'delete' | '.') {
    if (str === 'check') return this.checkAnswer();
    if (str === 'delete') return this.deleteNoteOrDot();
    if (str === '.') return this.addDot();
    return this.addRhythm(str);
  }

  protected addDot() {
    if (!this.isDotPossible()) return;
    this.getLastNoteHolder().rhythm!.addDots(1);
    this.renderAndUpdateButtonState();
  }

  protected addRhythm(baseDuration: BaseDuration | `${BaseDuration}r`) {
    const isRest = baseDuration.includes('r');
    baseDuration = baseDuration.replace('r', '') as BaseDuration;
    // Create and push a new NoteHolder onto the staff
    const note = this.music!.staves[0]!.clef.getNoteAtLine(3);
    const nh = new NoteHolder(isRest ? '' : note, baseDuration);
    nh.rest = isRest;
    this.music!.voices[0]!.measures[0]!.push(nh);

    this.renderAndUpdateButtonState();
  }

  private deleteNoteOrDot() {
    if(!this.isDeletePossible()) return;
    console.log('delete possible');
    const rhythm = this.getLastNoteHolder().rhythm!;
    if (rhythm.dots > 0) {
      console.log('setting dots');
      rhythm.setDots(rhythm.dots - 1)
    } else {
      console.log('splicing noteholder');
      this.music!.voices[0]!.measures[0]?.splice(this.getLastNoteHolder(), 1);
    }

    this.renderAndUpdateButtonState();
  }

  private getNoteHolders(): NoteHolder[] {
    return this.music!.voices[0]!.measures[0]!.noteHolders.filter(nh => !(nh.isNeitherNoteNorRest()));
  }

  private getLastNoteHolder(): NoteHolder {
    return this.getNoteHolders().slice(-1)[0]!;
  }

  private isDeletePossible() {
    return this.getNoteHolders().length > this.currentQuestion.fixedNotesCount;
  }

  private isDotPossible() {
    return this.isDeletePossible() && (this.getLastNoteHolder().rhythm!.dots < 2)
  }

  private disableOrEnableButtons() {
    const dotDisabled = !this.isDotPossible();
    const deleteDisabled = !this.isDeletePossible();
    console.log({ dotDisabled, deleteDisabled })
    this.rhythmButtonPanel.$set({
      dotDisabled,
      deleteDisabled
    });
  }

  generateQuestions(questions: string[] | undefined = this.options.questions): void {
    if (!this.options.drill && !(Array.isArray(questions) && questions.length)) {
      throw Error('Questions must be provided when not in drill mode for completeTheMeasure.');
    }
    const maxQuestions = this.options.drill
      ? defaultsDeep(this.options, { maxQuestions: 10 }).maxQuestions
      : questions?.length || 10;

    // If manual is true, notes aren't removed from the original question string
    function newQuestion(
      q: QuestionObject | string,
      i: number,
      manual: boolean
    ): CompleteTheMeasureQuestion {
      return new CompleteTheMeasureQuestion(q, i, manual);
    }

    if (this.options.drill) {
      this.questionData.sort((a, b) => {
        // sort by least performed
        const aHist = this.uPrefs!.getQuestionHistory(a.id!);
        const bHist = this.uPrefs!.getQuestionHistory(b.id!);
        const aCount = aHist ? aHist.attempts : 0;
        const bCount = bHist ? bHist.attempts : 0;
        // return -1 to keep a first, +1 to swap, 0 if equal:
        return aCount - bCount;
      });
    }

    this.questions = this.options.drill
      ? this.questionData
          .slice(-maxQuestions)
          .map((question, index) => newQuestion(question, index, false))
      : questions!.map((question, index) => newQuestion(question, index, true));

    this.questionPrompt = 'Add notes to complete the measure.';
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>{question} of ${this.questions.length}</small`;
    }
  }

  newQuestion(): void {
    super.newQuestion();
    console.log('setting disabled false')
    this.rhythmButtonPanel.$set({ disabled: false });

    // Rendering the music on the staff
    this.music = this.currentQuestion.music;
    const measure = this.music.voices[0]!.measures[0]!;
    const totalBeats = measure.getTotalBeats();
    const timeSignature = this.music.getTimeSignature(0)!;
    const remainingBeats = timeSignature.measureFraction.clone().subtract(totalBeats);
    const subdivision = timeSignature.subdivisionDuration.fraction;
    const smallestSubdivision = measure.noteHolders.reduce(
      (a, b) => (a.rhythm!.fraction.lessThan(b.rhythm!.fraction) ? a : b),
      new NoteHolder('C4', 'b')
    ).rhythm!.fraction;
    const divisor = subdivision.lessThan(smallestSubdivision) ? subdivision : smallestSubdivision;
    const expectedNotes = remainingBeats.divide(divisor).valueOf();

    this.expectedNotes = ~~expectedNotes + 5;
    this.editor!.renderer.music = this.music;
    this.editor!.renderer.render();
    this.resizeEditor();

    this.disableOrEnableButtons();

    // Saves the questionSVG if in test mode
    if (this.options.testMode) this.questionSVG = this.editor!.renderer.exportSVG();
  }

  resize() {
    return super.resize().then(() => {
      this.layout.rhythmValues.div!.style.height = '';
    })
  }

  /**
   * Resize editor based on current number of notes present on staff
   */
  protected resizeEditor(): void {
    const numVisibleNH = this.music!.voices[0]!.measures[0]!.noteHolders.reduce(
      (total, nh) => total + (nh.isNeitherNoteNorRest() ? 0 : 1),
      0
    );
    const notesToPad = Math.max(this.expectedNotes, numVisibleNH);
    const { clientWidth } = this.editor!.renderer.div;
    // 20 pix min for each note (2-note buffer) 40 pix for clef and timeSignature
    const noteWidth =
      clientWidth > 1000 ? 40 : clientWidth > 800 ? 30 : clientWidth > 500 ? 25 : 20;
    const width = noteWidth * (notesToPad + EDITOR_BUFFER) + 40;
    this.editor!.options.fixedWidth = width;
    this.editor!.renderer.options.fixedWidth = width;
    this.editor!.renderer.render();
    // this.resize();
    const height = this.editor!.renderer.div.clientHeight;
    this.editor!.renderer.responsiveResize(height);
    this.editor!.renderer.div.style.height = `${height}px`;
  }

  checkAnswer(): void {
    const timeSignature = this.music.getTimeSignature()!;
    const correct = this.music.voices[0]!.measures[0]!.isComplete(timeSignature, true);

    if (this.options.testMode) {
      return this.checkpointAnswer(correct);
    }
    if (this.rightAnswerDelay) return undefined;

    // When correct, call rightAnswer();
    if (correct) {
      if (this.uPrefs) {
        const question = this.currentQuestion;
        this.uPrefs.saveQuestion(question.id, 1 / (this.wrongAnswerCount + 1)).catch((err) => {
          console.error(err);
        });
      }
      console.log('setting disabled true')
      this.rhythmButtonPanel.$set({ disabled: true });
      return this.rightAnswer(this.currentQuestion.stopTime * 1000 + 1000);
    }

    // When incorrect, call wrongAnswer();
    this.wrongAnswerCount++;
    return this.wrongAnswer();
  }

  newCheckpointQuestion(question: string): Promise<void> {
    return this.loadingPromise.then(() => {
      this.generateQuestions([question]);
      this.questionNumber = 0;
      return Promise.resolve(this.newQuestion());
    });
  }

  playAnswer(): void {
    this.clearGhostingNH();
    MeterID.prototype.playAnswer.call(this);
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(correct: boolean): AnswerAndData {
    const answerAndData = {
      question: this.currentQuestion.rhythmOrMusicString,
      questionType: 'completeTheMeasure',
      questionText: this.currentInstructions!,
      questionSVG: this.questionSVG,
      studentAnswerSVG: this.editor!.renderer.exportSVG(),
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  static registry: InteractiveRegistryEntry = {
    description: 'Complete the measure',
    skills: ['meter_completion_simple', 'meter_completion_compound', 'meter_completion_cut_time'],
  };
}
